import pluralize from "pluralize";
import { SocialCoachSession } from "@/services/bedrock";
import { getRedirectForPlayer } from "@/utils/helpers";

const player = "/player";

export const mediaFirebaseUrl = (coachId: string): string => `coaches/${coachId}/prompts/media`;
export const productMediaFirebaseUrl = (
  product: "videoProject" | "teleprompter" | "post" | "videoPage" | "playerCard" | "screenCapture",
  accountId: string | number,
  accountType: "coach" | "player" | "user" = "player"
): string => `${product}/${pluralize(accountType, 2)}/${accountId}/media`;

export const mediaEditedFirebaseUrl = (coachId: string | number): string => `coaches/${coachId}/prompts/mediaEdited`;

export const userDefaultRedirectUrl = (session?: SocialCoachSession): string => {
  switch (session?.userRole) {
    case "PLAYER":
      return getRedirectForPlayer(session) || URLS.player.dashboard;
    case "COACH":
    case "ASSISTANT_COACH":
      return URLS.coach.dashboard;
    case "ADMIN":
      return URLS.admin.dashboard;
    case "EDITOR":
      return editorQueueListUrl();
    default:
      return "/";
  }
};

export const URLS = {
  dashboard: "",
  socialHome: "https://socialcoach.io",
  home: "/",
  login: "/sign-in",
  logout: "/logout",
  signInSSO: "/sign-in-sso",
  pkceAuth: "/pkce-auth",
  register: "/sign-up",
  forgotPassword: "/forgot-password",
  changePassword: "/reset-password/:userId/:actionToken",
  activatePlayerUser: "/activate/player/:userId/:actionToken",
  activateUser: "/activate/:userId/:actionToken",
  resendEmail: "/resend-verify-account",
  notFound: "/notfound",
  videoScripts: "/scripts",
  newVideoScript: "/scripts/new",
  videoScript: "/scripts/:id",

  coach: {
    dashboard: "/dashboard",
    calendar: "/calendar",
    compliance: "/compliance",
    campaigns: "/campaigns",
    newCampaigns: "/campaigns/new/",
    editCampaigns: "/campaigns/:campaignId",
    alerts: "/alerts",
    help: "https://www.socialcoach.io/coach-resource-hub",
    playerCards: "/player-cards",
    recordVideo: "/record-video",
    screenCapture: "/screen-capture",

    // LIBRARY
    library: "/library/",
    libraryTab: "/library/:tab?",
    libraryTabWithId: "/library/:tab?/:id?",

    prompts: "/library/prompts",
    newPrompt: "/library/prompts/new",
    prompt: "/library/prompts/:promptId",

    videoScripts: "/library/scripts",
    newVideoScript: "/library/scripts/new",
    videoScript: "/library/scripts/:id",

    videoProjects: "/library/video-projects",
    newVideoProject: "/library/video-projects/new",
    videoProject: "/library/video-projects/:id",

    videoPages: "/library/video-pages",

    blogs: "/library/blogs",
    blog: "/library/blogs/:id",

    superPages: "/library/super-pages",
    newSuperPage: "/library/super-pages/new",
    superPage: "/library/super-pages/:id",

    // USERS
    users: "/users/",
    usersTab: "/users/:tab?",

    players: "/users/players",
    newPlayer: "/users/players/new",
    editPlayer: "/users/players/:playerId",
    editPlayerTab: "/users/players/:playerId/:tab?",

    assistants: "/users/assistants",
    newAssistant: "/users/assistants/new",
    editAssistant: "/users/assistants/:userId",

    // REPUTATION
    reputation: {
      root: "/reputation/",
      tab: "/reputation/:tab?",
      overview: "/reputation/overview",
      fans: "/reputation/fans",
      reviews: "/reputation/reviews",
      forms: "/reputation/forms",
    },

    // SETTINGS
    settings: "/settings/",
    settingsTab: "/settings/:tab?",
    mvpSettings: "/settings/mvp",
    apiKeys: "/settings/api-keys",
    companyInfo: "/settings/company-info",
    platformSettings: "/settings/platform-settings",
    digestEmails: "/settings/digest-emails",
  },
  player: {
    dashboard: player + "/dashboard",
    calendar: player + "/calendar",
    embeddedSocialAccounts: player + "/social-embedded",
    paymentInfo: player + "/payment-details",
    firstLogin: player + "/first-login",
    firstPicture: player + "/first-picture",
    firstSocialNetworks: player + "/first-social-networks",
    firstCongrats: player + "/congrats",
    success: player + "/success",
    post: player + "/post",
    compliance: player + "/compliance",
    recordVideo: player + "/record-video",
    screenCapture: player + "/screen-capture",
    playerCards: player + "/player-cards",

    // LIBRARY
    library: player + "/library/",
    libraryTab: player + "/library/:tab?",
    libraryTabWithId: player + "/library/:tab?/:id?",

    prompts: player + "/library/prompts",

    videoScripts: player + "/library/scripts",
    newVideoScript: player + "/library/scripts/new",
    videoScript: player + "/library/scripts/:id",

    videoProjects: player + "/library/video-projects",
    newVideoProject: player + "/library/video-projects/new",
    videoProject: player + "/library/video-projects/:id",

    videoPages: player + "/library/video-pages",
    superPages: player + "/library/video-page-assignments",
    blogs: player + "/library/blogs",
    blogHome: player + "/library/blog-home",
    blog: player + "/library/blogs/:id",
    reputation: {
      root: player + "/reputation/",
      tab: player + "/reputation/:tab?",
      inbox: player + "/reputation/inbox",
      reviews: player + "/reputation/reviews",
      forms: player + "/reputation/forms",
      respond: player + "/reputation/inbox/:reviewId",
    },

    // SETTINGS
    settings: player + "/settings/:tab?",
    settingsProfile: player + "/settings/profile",
    settingsCustomizations: player + "/settings/customizations",
    settingsSocialNetworks: player + "/settings/social-networks",
    mvpSettings: player + "/settings/mvp",
  },
  admin: {
    dashboard: "/admin/dashboard",
    prospects: "/admin/prospects",
    players: "/admin/players",
    coachCalendar: "/admin/coaches/:coachId/calendar",
    login: "/admin/sign-in",
    leaderboards: "/admin/leaderboards",
  },
  editor: {
    videoProjects: {
      root: "/editor/video-projects/",
      grid: "/editor/video-projects/grid",
      board: "/editor/video-projects/board",
      detail: "/editor/video-projects/:id",
    },
    videoScripts: "/editor/scripts",
    videoScript: "/editor/scripts/:id",
    newVideoScript: "/editor/scripts/new",
  },
};

export type EditorQueueView = "grid" | "board";

export const EDITOR_QUEUE_VIEW_STORAGE_KEY = "editingQueueList-viewMode";

export function editorQueueViewFromPath(pathname: string): EditorQueueView {
  return pathname === URLS.editor.videoProjects.board ? "board" : "grid";
}

export function editorVideoProjectDetailUrl(id: number | string, options?: { qa?: boolean }): string {
  const path = URLS.editor.videoProjects.detail.replace(":id", String(id));
  return options?.qa ? `${path}?qa=1` : path;
}

export function editorQueueListUrl(view?: EditorQueueView): string {
  const resolvedView =
    view ??
    (typeof localStorage !== "undefined" && localStorage.getItem(EDITOR_QUEUE_VIEW_STORAGE_KEY) === "board"
      ? "board"
      : "grid");
  return resolvedView === "board" ? URLS.editor.videoProjects.board : URLS.editor.videoProjects.grid;
}

export const blogListUrl = (isPlayer: boolean): string => (isPlayer ? URLS.player.blogs : URLS.coach.blogs);

export const blogDetailUrl = (isPlayer: boolean, id: number | string): string =>
  (isPlayer ? URLS.player.blog : URLS.coach.blog).replace(":id", String(id));
